import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

const LABEL_COLORS = {
  great: "#A3B899",
  good: "#B8C4A3",
  neutral: "#D4A574",
  stressed: "#E8C28A",
  frustrated: "#E8B4A0",
  exhausted: "#D98074",
  anxious: "#E8B4A0",
  sad: "#9A938A",
};

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Mood() {
  const [data, setData] = useState({ entries: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/mood?days=30")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const chartData = data.entries.map((e) => ({
    date: fmtDate(e.created_at),
    score: Number(e.score.toFixed(2)),
    label: e.label,
  }));

  return (
    <div data-testid="mood-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 anim-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-2">
            Vibe over time
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
            How have your weeks been feeling?
          </h1>
          <p className="mt-3 text-[#6B655D] max-w-2xl">
            Each check-in becomes a quiet signal of how your work life is going. No
            judgment — just a thread to notice patterns.
          </p>
        </header>

        <div className="surface-card p-6 sm:p-8 mb-10 anim-fade-up anim-delay-1">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#9A938A]">
                30-day average
              </p>
              <p
                data-testid="mood-average"
                className="font-serif text-4xl text-[#2D2926]"
              >
                {data.average.toFixed(2)}
              </p>
            </div>
            <p className="text-sm text-[#6B655D]">
              {data.count} check-in{data.count === 1 ? "" : "s"}
            </p>
          </div>
          <div className="h-72" data-testid="mood-chart">
            {loading ? (
              <p className="text-[#6B655D]">Loading…</p>
            ) : chartData.length === 0 ? (
              <p className="text-[#6B655D]">
                Once you start chatting with your Colleagues, your mood signal will appear here.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#E6E2D8" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#9A938A" fontSize={12} />
                  <YAxis domain={[-1, 1]} stroke="#9A938A" fontSize={12} />
                  <ReferenceLine y={0} stroke="#D4A574" strokeDasharray="2 4" />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #E6E2D8",
                      borderRadius: 12,
                      fontFamily: "Outfit, sans-serif",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#C29260"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#C29260", strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <section>
          <h2 className="font-serif text-3xl text-[#2D2926] mb-4">Recent check-ins</h2>
          {data.entries.length === 0 ? (
            <p className="text-[#6B655D]">Nothing logged yet.</p>
          ) : (
            <ul
              data-testid="mood-list"
              className="surface-card divide-y divide-[#E6E2D8] overflow-hidden"
            >
              {[...data.entries].reverse().slice(0, 30).map((e) => (
                <li
                  key={e.entry_id}
                  className="px-6 py-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden
                      className="w-3 h-3 rounded-full"
                      style={{ background: LABEL_COLORS[e.label] || "#D4A574" }}
                    />
                    <span className="text-[#2D2926] capitalize">{e.label}</span>
                  </div>
                  <div className="text-xs text-[#9A938A] uppercase tracking-wider">
                    {new Date(e.created_at).toLocaleString()}
                    <span className="ml-3 text-[#6B655D] normal-case">
                      {e.score.toFixed(2)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
